import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, ArrowDown, Loader2,
  Flame, HeartHandshake, Users, HeartPulse, PersonStanding, Flower2,
} from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage, Page, Meta } from "../components/Reveal";
import MarqueeEditorial from "../components/MarqueeEditorial";
import { postNewsletter } from "../lib/api";
import { useContent } from "../lib/ContentContext";

const ARCH = "rounded-t-[999px] rounded-b-[2rem]";
const SOFT = "rounded-[2rem]";

const FEATURED_SLUG = "ralentir-pour-mieux-ressentir-le-slow-sex";

function CircularBadge() {
  const c = useContent();
  return (
    <div
      data-testid="hero-circular-badge"
      className="absolute -bottom-8 -left-8 hidden h-32 w-32 items-center justify-center rounded-full bg-cream shadow-[0_10px_40px_-15px_rgba(36,56,47,0.3)] md:flex"
    >
      <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow p-2" aria-hidden="true">
        <defs>
          <path id="badge-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
        </defs>
        <text className="fill-charcoal text-[8px] font-medium uppercase" style={{ letterSpacing: "0.22em" }}>
          <textPath href="#badge-circle">{c("home.badge.texte")}</textPath>
        </text>
      </svg>
      <span className="absolute h-2.5 w-2.5 rounded-full bg-clay" aria-hidden="true" />
    </div>
  );
}

function Newsletter() {
  const c = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postNewsletter({ email });
      toast.success("Bienvenue dans la conversation. À très bientôt !");
      setEmail("");
    } catch {
      toast.error("Une erreur est survenue. Vérifiez votre courriel.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="infolettre" data-testid="newsletter-section" className="scroll-mt-24 bg-sage/50 py-24 md:py-32">
      <div className="mx-auto max-w-2xl px-6 text-center">
        <FadeUp>
          <h2 className="font-serif text-4xl font-light tracking-tight md:text-5xl">
            {c("home.newsletter.t1")} <em className="text-clay">{c("home.newsletter.t2")}</em>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-6 text-base leading-relaxed text-smoke md:text-lg">
            {c("home.newsletter.p")}
          </p>
        </FadeUp>
        <FadeUp delay={0.2}>
          <form data-testid="newsletter-form" onSubmit={onSubmit} className="mx-auto mt-10 flex max-w-md items-end gap-4">
            <input
              type="email"
              required
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={c("home.newsletter.placeholder")}
              className="w-full border-b border-charcoal/25 bg-transparent py-3 text-base text-charcoal placeholder:text-ash/70 outline-none transition-colors duration-300 focus:border-clay"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              disabled={loading}
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>{c("home.newsletter.bouton")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  const c = useContent();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 400);
  }, [hash]);

  const CATEGORIES = [
    { Icon: Flame, title: c("home.cat.1.titre"), text: c("home.cat.1.texte") },
    { Icon: HeartHandshake, title: c("home.cat.2.titre"), text: c("home.cat.2.texte") },
    { Icon: Users, title: c("home.cat.3.titre"), text: c("home.cat.3.texte") },
    { Icon: HeartPulse, title: c("home.cat.4.titre"), text: c("home.cat.4.texte") },
    { Icon: PersonStanding, title: c("home.cat.5.titre"), text: c("home.cat.5.texte") },
    { Icon: Flower2, title: c("home.cat.6.titre"), text: c("home.cat.6.texte") },
  ];

  const CREDIBILITE = [1, 2, 3, 4, 5].map((n) => c(`home.credibilite.${n}`));
  const MEDIA_LOGOS = [1, 2, 3, 4, 5].map((n) => c(`home.credibilite.media.${n}`));

  const APPEARANCES = [1, 2, 3].map((n) => ({
    media: c(`home.apparition.${n}.media`),
    type: c(`home.apparition.${n}.type`),
    title: c(`home.apparition.${n}.titre`),
    date: c(`home.apparition.${n}.date`),
    img: c(`home.apparition.${n}.img`),
  }));

  return (
    <Page testId="home-page">
      {/* 1. HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40">
        <div className="pointer-events-none absolute -top-24 -left-32 h-[28rem] w-[28rem] rounded-full bg-sand/50 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute top-40 right-[-8rem] h-[24rem] w-[24rem] rounded-full bg-sage/50 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-8">
          <div className="flex flex-col justify-center lg:col-span-7">
            <FadeUp delay={0.15} y={14}>
              <p className="sr-only">Eugénie Larrivée</p>
            </FadeUp>
            <h1 className="mt-6 font-serif text-5xl font-light leading-[1.06] tracking-tight text-charcoal md:text-7xl xl:text-[5.25rem]">
              <MaskedLine delay={0.3}>{c("home.hero.l1")}</MaskedLine>
              <MaskedLine delay={0.45}>{c("home.hero.l2")}</MaskedLine>
              <MaskedLine delay={0.6} className="italic text-clay">{c("home.hero.accent")}</MaskedLine>
            </h1>
            <FadeUp delay={0.9} className="mt-8">
              <p className="text-base leading-relaxed text-smoke md:text-lg">
                {c("home.hero.role1")}
                <br />
                <span className="text-ash">{c("home.hero.role2")}</span>
              </p>
            </FadeUp>
            <FadeUp delay={1.05} className="mt-10">
              <button
                data-testid="hero-cta-univers"
                onClick={() => document.getElementById("univers")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal"
              >
                {c("home.hero.cta")}
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
              <p className="mt-6 text-xs uppercase tracking-[0.22em] text-ash">
                {c("home.hero.tags")}
              </p>
            </FadeUp>
          </div>

          <div className="relative pb-20 lg:col-span-5 lg:pb-10">
            <FadeUp delay={0.8} y={40}>
              <div className="relative ml-auto max-w-md">
                <div className={`absolute -bottom-6 -right-6 h-full w-full bg-sand ${ARCH}`} aria-hidden="true" />
                <ParallaxImage
                  src={c("home.hero.img")}
                  alt="Portrait d'Eugénie Larrivée"
                  className={`relative aspect-[3/4] shadow-[0_30px_80px_-35px_rgba(36,56,47,0.45)] ${ARCH}`}
                  speed={7}
                  position="50% 18%"
                />
                <CircularBadge />
              </div>
            </FadeUp>
          </div>
        </div>

        <FadeUp delay={1.5} className="relative mx-auto mt-8 flex max-w-7xl items-center gap-3 px-6 pb-10 md:px-12">
          <span className="relative block h-10 w-px overflow-hidden bg-linen">
            <span className="absolute inset-0 animate-scroll-hint bg-clay" />
          </span>
          <span className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ash">
            Défiler <ArrowDown className="h-3 w-3" />
          </span>
        </FadeUp>
      </section>

      {/* 2. LA SEXUALITÉ EST COMPLEXE */}
      <section id="univers" data-testid="complexity-section" className="mx-auto max-w-7xl scroll-mt-32 px-6 py-24 md:px-12 md:py-36">
        <FadeUp>
          <h2 className="mx-auto max-w-4xl text-center font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-6xl">
            {c("home.complexity.t1")}
            <br />
            <em className="text-clay">{c("home.complexity.t2")}</em>
          </h2>
        </FadeUp>
        <div className="mt-20 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <FadeUp key={cat.title} delay={0.07 * i}>
              <div data-testid={`category-${cat.title.toLowerCase().replace(/\s/g, "-")}`} className="group border-t border-linen pt-8 transition-colors duration-300">
                <cat.Icon className={`h-7 w-7 transition-transform duration-500 group-hover:-translate-y-1 ${i % 2 === 1 ? "text-moss" : "text-clay"}`} strokeWidth={1.25} />
                <h3 className="mt-5 font-serif text-2xl font-light tracking-tight">{cat.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-smoke">{cat.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* 3. BONJOUR, JE SUIS EUGÉNIE — PRATIQUE CLINIQUE */}
      <section data-testid="bonjour-section" className="relative overflow-hidden bg-shell py-24 md:py-36">
        <div className="pointer-events-none absolute bottom-0 left-[-10rem] h-[24rem] w-[24rem] rounded-full bg-sage/40 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5" y={40}>
            <div className="relative mx-auto max-w-md">
              <div className={`absolute -bottom-6 -right-6 h-full w-full bg-sand/60 ${ARCH}`} aria-hidden="true" />
              <ParallaxImage
                src={c("home.bonjour.img")}
                alt="Eugénie Larrivée dans son espace clinique"
                className={`relative aspect-[4/5] ${ARCH}`}
                speed={6}
                position="50% 18%"
              />
            </div>
          </FadeUp>
          <div className="lg:col-span-7">
            <FadeUp><Meta>{c("home.bonjour.meta")}</Meta></FadeUp>
            <h2 className="mt-4 font-serif text-4xl font-light tracking-tight md:text-6xl">
              <MaskedLine inView>{c("home.bonjour.t1")} <em className="text-clay">{c("home.bonjour.t2")}</em></MaskedLine>
            </h2>
            <FadeUp delay={0.15}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
                {c("home.bonjour.p1")}
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
                {c("home.bonjour.p2")}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <Link to="/clinique" data-testid="clinique-cta"
                  className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                  {c("home.bonjour.cta1")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/eugenie" data-testid="bonjour-cta"
                  className="group inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
                  {c("home.bonjour.cta2")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 4. CRÉDIBILITÉ */}
      <section data-testid="credibilite-section" className="border-y border-linen py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp>
            <p className="text-center text-xs font-medium uppercase tracking-[0.25em] text-ash">
              {c("home.credibilite.label")}
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
              {CREDIBILITE.map((x) => (
                <span key={x} className="font-serif text-lg font-light italic text-charcoal md:text-xl">{x}</span>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-linen pt-8">
              {MEDIA_LOGOS.map((m) => (
                <span key={m} className="text-sm font-medium uppercase tracking-[0.15em] text-ash/80">{m}</span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>


      {/* 6. EN CE MOMENT, ON PARLE DE… */}
      <section data-testid="topics-section" className="bg-shell py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 md:px-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-8">
            <Meta>{c("home.topics.meta")}</Meta>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-light leading-snug tracking-tight md:text-5xl">
              {c("home.topic.1.titre")}
            </h2>
            <p className="mt-5 text-xs font-medium uppercase tracking-[0.25em] text-clay">{c("home.topic.1.cat")}</p>
          </FadeUp>
          <FadeUp delay={0.15} className="flex flex-col items-start gap-6 lg:col-span-4 lg:items-end">
            <Link to={`/conversations/${FEATURED_SLUG}`} data-testid="topic-card-1"
              className="group inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
              {c("home.topic.1.lien")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/conversations" data-testid="topics-see-all"
              className="group inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
              {c("home.topics.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* 7. PROJETS */}
      <section data-testid="projets-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp><Meta>{c("home.projets.meta")}</Meta></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
            {c("home.projets.t1")} <em className="text-clay">{c("home.projets.t2")}</em>
          </h2>
        </FadeUp>
        <div className="mt-16 grid gap-8 md:grid-cols-2 md:gap-12">
          <FadeUp className="h-full">
            <Link to="/projets" data-testid="project-card-sexualis"
              className={`group flex h-full flex-col overflow-hidden bg-sage/30 transition-colors duration-500 hover:bg-sage/50 ${SOFT}`}>
              <ParallaxImage src={c("home.projet.1.img")} alt={c("home.projet.1.titre")} className="aspect-[16/10]" speed={6} />
              <div className="flex flex-1 flex-col p-10">
                <h3 className="font-serif text-3xl font-light tracking-tight">{c("home.projet.1.titre")}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke md:text-base">
                  {c("home.projet.1.texte")}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-charcoal transition-colors duration-300 group-hover:text-clay">
                  {c("home.projet.cta")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </FadeUp>
          <FadeUp delay={0.15} className="h-full md:mt-16">
            <Link to="/projets" data-testid="project-card-fesses"
              className={`group flex h-full flex-col overflow-hidden bg-sand/40 transition-colors duration-500 hover:bg-sand/60 ${SOFT}`}>
              <ParallaxImage src={c("home.projet.2.img")} alt={c("home.projet.2.titre")} className="aspect-[16/10]" speed={6} />
              <div className="flex flex-1 flex-col p-10">
                <h3 className="font-serif text-3xl font-light tracking-tight">{c("home.projet.2.titre")}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-smoke md:text-base">
                  {c("home.projet.2.texte")}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-charcoal transition-colors duration-300 group-hover:text-clay">
                  {c("home.projet.cta")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* 8. MÉDIAS */}
      <section data-testid="medias-section" className="bg-sage/30 py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <FadeUp>
              <Meta>{c("home.medias.meta")}</Meta>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
                {c("home.medias.t1")} <em className="text-clay">{c("home.medias.t2")}</em>
              </h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <Link to="/medias" data-testid="medias-see-all"
                className="group inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
                {c("home.medias.seeall")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {APPEARANCES.map((a, i) => (
              <FadeUp key={a.title} delay={0.08 * i} className="h-full">
                <Link to="/medias" data-testid={`appearance-card-${i + 1}`}
                  className="group flex h-full flex-col bg-transparent">
                  <div className="aspect-[16/10] overflow-hidden rounded-[1.75rem]">
                    <img src={a.img} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <p className="mt-5 text-xs font-medium uppercase tracking-[0.2em] text-clay">{a.media} · {a.type}</p>
                  <h3 className="mt-3 flex-1 font-serif text-xl font-light leading-snug tracking-tight md:text-2xl">{a.title}</h3>
                  <p className="mt-3 text-xs text-ash">{a.date}</p>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* 9. CONFÉRENCES */}
      <section data-testid="conferences-section" className="relative overflow-hidden mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <div className="pointer-events-none absolute top-20 right-[-10rem] h-[22rem] w-[22rem] rounded-full bg-sand/40 blur-3xl" aria-hidden="true" />
        <div className="relative grid items-center gap-14 lg:grid-cols-12">
          <FadeUp className="lg:col-span-6" y={40}>
            <div className="relative">
              <div className={`absolute -bottom-6 -right-6 h-full w-full bg-sand/70 ${SOFT}`} aria-hidden="true" />
              <ParallaxImage src={c("home.conferences.img")} alt="Eugénie Larrivée sur scène" className={`relative aspect-[16/11] ${SOFT}`} speed={8} position="50% 12%" />
            </div>
          </FadeUp>
          <div className="lg:col-span-6">
            <FadeUp><Meta>{c("home.conferences.meta")}</Meta></FadeUp>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              <MaskedLine inView>{c("home.conferences.t1")}</MaskedLine>
              <MaskedLine inView delay={0.12} className="italic text-clay">{c("home.conferences.t2")}</MaskedLine>
            </h2>
            <FadeUp delay={0.2}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-smoke md:text-lg">
                {c("home.conferences.p")}
              </p>
              <Link to="/conferences" data-testid="conferences-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                {c("home.conferences.cta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 10. INFOLETTRE */}
      <Newsletter />

      {/* 11. SIGNATURE DE FIN */}
      <section data-testid="signature-section" className="mx-auto max-w-7xl px-6 py-28 text-center md:px-12 md:py-40">
        <FadeUp>
          <div className="mx-auto h-28 w-28 overflow-hidden rounded-full">
            <img src={c("home.signature.img")} alt="" className="h-full w-full object-cover" />
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-12 font-hand text-3xl leading-snug text-clay md:text-5xl">
            {c("home.signature.l1")}
            <br />
            {c("home.signature.l2")}
            <br />
            {c("home.signature.l3")}
          </p>
        </FadeUp>
        <FadeUp delay={0.3}>
          <Link to="/conversations" data-testid="signature-cta"
            className="group mt-12 inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
            {c("home.signature.cta")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </section>

      {/* 12. BANNIÈRE DÉROULANTE — PRÈS DU PIED DE PAGE */}
      <MarqueeEditorial />
    </Page>
  );
}
